import { useEffect, useRef, useState } from "react";
import Button from "../../components/Button/button";
import { appendSourceParameter, formatBillingPeriodForGTM, sendEvent } from "../../../gtm/functions";
import { IGTMEventType } from "../../../gtm/IGTMEventType";
import ChevronsRightIcon from "../../shared-components/icons/ChevronsRightIcon";

const Subscription = ({
  metaData,
  subscriptionName,
  features,
  priceDetails,
  description,
  productId,
  isUpsellModal = false,
place = "",}: {
  metaData: [string, string][];
  subscriptionName: string;
  features: string[];
  priceDetails: Price;
  description: string;
  productId: string;
  isUpsellModal?: boolean;place?: string;
}) => {
  const [showEntriesPopover, setShowEntriesPopover] = useState<boolean>(false);
  const subscriptionMetaData: Record<string, string> = Object.fromEntries(metaData);
  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState<boolean>(window.innerWidth >= 1024);
  const ref = useRef<HTMLDivElement>();

  useEffect(() => {
    document.addEventListener("click", documentClickEventHandler);
    document.addEventListener("mouseup", documentClickEventHandler);
    document.addEventListener("touchup", documentClickEventHandler);
    renderRespectiveLayout();
    return () => {
      document.removeEventListener("click", documentClickEventHandler);
      document.removeEventListener("mouseup", documentClickEventHandler);
      document.removeEventListener("touchup", documentClickEventHandler);
    };
  }, []);

  const documentClickEventHandler = (e: Event) => {
    const target = e.target as HTMLElement;
    if (!ref.current?.contains(target)) {
      setShowEntriesPopover(false);
    }
  };

  const renderRespectiveLayout = () => {
    setIsViewDetailsOpen(window.innerWidth >= 1024);
  };

  const getBtnType = function (type: string) {
    if (type === "Essential") {
      return "border-primary text-primary";
    } else if (type === "Premium") {
      return "border-transparent bg-[#3E5FFF] text-white";
    } else if (type === "Ultimate") {
      return "border-transparent bg-[#FC9E2F] text-black";
    }
  };

  const getShowHowEntriesWorkPopover = () => (
    <div className="absolute flex flex-col gap-2 w-[248px] rounded-lg bg-white shadow-[0_1.5px_8px_0_rgba(0,0,0,0.20)] px-4 py-3 -top-[135px]">
      <span className="text-[#49454F] text-[14px] font-semibold font-jost">How entries work</span>
      <div
        dangerouslySetInnerHTML={{
          __html: subscriptionMetaData["how_it_works"],
        }}
        className="text-[12px] text-[#898994] font-normal"
      />
    </div>
  );

  // const getSvgElement = () => {
  //     const svgDoc = parseFromString(
  //         subscriptionMetaData["tag_icon_url"],
  //         "image/svg+xml",
  //     );
  //
  //     return svgDoc.documentElement.outerHTML;
  // };

  const getSubscriptionHeaderData = () => (
    <div
      className={`flex font-jost items-center pt-2 pb-1 rounded-t-2xl lg:pt-3 lg:pb-1 ${subscriptionMetaData["tag_text"] === "NULL" ? "max-lg:hidden" : ""}`}>
      {subscriptionMetaData["tag_text"] !== "NULL" ? (
        <div
          className={`text-center uppercase w-full text-[13px] font-medium leading-[18px]  ${subscriptionName === "Premium" && "text-white"}`}>
          {subscriptionMetaData["tag_text"]}
        </div>
      ) : (
        <div className={`text-center uppercase w-full text-[13px] font-medium leading-[18px] opacity-0 invisible`}>
          Essential
        </div>
      )}
    </div>
  );

  const getSubscriptionBodyData = () => (
    <div className={`flex items-center flex-col`}>
      <div className={"self-start w-full mb-4"}>
        {/*plan type title*/}
        <div className={"w-full flex items-center justify-between mb-1 font-jost"}>
          <div className={"text-black text-lg leading-6 font-medium"}>{subscriptionName} Plan</div>

          <div className="flex relative overflow-hidden rounded-full p-px w-fit">
            <div className="bg-[#FBEDEC] text-red-2 relative flex z-[1] body-xs-medium px-2 py-1 rounded-full group/badge">
              <span className="break-normal text-center text-xs text-red-2 font-medium leading-4">
                Save {priceDetails.metadata.discount_value_card}
              </span>
            </div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%+4px)] aspect-square rounded-full pointer-events-none bg-beam animate-beamSpin z-[0]"></div>
          </div>
        </div>

        {/*plan cost per month*/}
        <div className={"flex items-center gap-2 font-jost mb-1"}>
          <div className={"text-2xl font-bold leading-7.5"}>
            {priceDetails.interval === "month"
              ? `£${priceDetails.unit_amount / 100}`
              : priceDetails.metadata["monthly_price_card"]}
          </div>
          <div className={"text-xs text-dark-1 font-medium leading-4"}>
            <span className={"line-through text-sm leading-5 text-gray-1"}>{priceDetails.metadata.base_price}</span>
            /month
          </div>
        </div>

        <div className="text-[#4C4C52] font-jost text-[13px] leading-snug font-normal">
          {priceDetails.interval === "year" ? `£${priceDetails.unit_amount / 100} billed annually` : "\u00A0"}
        </div>
      </div>

      <div className={"w-full mb-6"}>
        <Button
          className={`min-h-[40px] flex justify-center items-center rounded px-4 py-1 border btnWithArrow w-full ${getBtnType(
            subscriptionName
          )}`}
          onClick={() => {
            sendEvent(
              isUpsellModal ? IGTMEventType.SubscriptionUpsellPlanSelect : IGTMEventType.SubscriptionPlanSelect,
              appendSourceParameter(isUpsellModal, {
                plan_id: subscriptionName.toLowerCase(),
                price:
                  priceDetails.interval === "month"
                    ? priceDetails.unit_amount / 100
                    : priceDetails.metadata["monthly_price_card"],
                billing_period: formatBillingPeriodForGTM(priceDetails.interval),
              })
            );
            const existingUrlParams = new URLSearchParams(window.location.search);
            const url = new URL(`${window.location.protocol}//${window.location.host}/subscription-checkout`);
            url.searchParams.append("packageId", priceDetails.id);
            if (isUpsellModal) {
              url.searchParams.append("upsell", "true");
              url.searchParams.append("utm_medium", "fp_upsell");
            } else if (place && typeof place === "string") {
              url.searchParams.append("utm_medium", place);
            }
            if (existingUrlParams.size > 0) {
              existingUrlParams.forEach((value, key) => {
                if (url.searchParams.has(key)) {
                  url.searchParams.set(key, value);
                  return;
                }
                url.searchParams.append(key, value);
              });
            }
            location.href = url.toString();
          }}
          text="">
          <span className="relative flex items-center font-jost text-base leading-snug font-medium gap-2">
            Get {subscriptionName}
            <span className={"relative size-5"}>
              <ChevronsRightIcon variant={3} className={"absolute inset-0 size-5 arrowImage"} />
            </span>
          </span>
        </Button>
      </div>

      {/*<div className="lg:hidden text-[#4C4C52] font-jost text-[13px] leading-[1.125rem] font-normal text-center">*/}
      {/*    {description}*/}
      {/*</div>*/}
    </div>
  );

  const getFeatures = () => (
    <div className="flex flex-col gap-4">
      <div ref={ref} className="flex gap-2 items-center relative">
        <span className="flex justify-center items-center gap-2 rounded [background:rgba(61,174,68,0.10)] font-jost text-[#3DAE44] px-2.5 py-1 text-sm font-medium leading-5 lg:text-base lg:leading-[22px]">
          {subscriptionMetaData["total_entries"]} Entries /month
        </span>
        <svg
          id="showEnteriesPopoverInfo"
          className="cursor-pointer"
          onClick={() => setShowEntriesPopover(true)}
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none">
          <path
            d="M8 6H8.00667M7.33333 8H8V10.6667H8.66667M2 8C2 8.78793 2.15519 9.56815 2.45672 10.2961C2.75825 11.0241 3.20021 11.6855 3.75736 12.2426C4.31451 12.7998 4.97595 13.2417 5.7039 13.5433C6.43185 13.8448 7.21207 14 8 14C8.78793 14 9.56815 13.8448 10.2961 13.5433C11.0241 13.2417 11.6855 12.7998 12.2426 12.2426C12.7998 11.6855 13.2417 11.0241 13.5433 10.2961C13.8448 9.56815 14 8.78793 14 8C14 6.4087 13.3679 4.88258 12.2426 3.75736C11.1174 2.63214 9.5913 2 8 2C6.4087 2 4.88258 2.63214 3.75736 3.75736C2.63214 4.88258 2 6.4087 2 8Z"
            stroke="#898994"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {showEntriesPopover && getShowHowEntriesWorkPopover()}
      </div>
      <div className="flex flex-col gap-3">
        {features.map((feature, index) => (
          <div key={index} className="flex gap-1 items-start">
            <div className="size-3 mt-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M4.82919 9.84588C4.73373 9.94481 4.6035 10 4.46821 10C4.33293 10 4.20269 9.94481 4.10724 9.84588L1.22438 6.87525C0.925206 6.56701 0.925206 6.06719 1.22438 5.75953L1.58536 5.38752C1.88463 5.07929 2.3692 5.07929 2.66838 5.38752L4.46821 7.24195L9.33162 2.23118C9.63089 1.92294 10.1159 1.92294 10.4146 2.23118L10.7756 2.60318C11.0748 2.91142 11.0748 3.41115 10.7756 3.7189L4.82919 9.84588Z"
                  fill={subscriptionName === "Ultimate" ? "#FC9E2F" : "#3DAE44"}
                />
              </svg>
            </div>
            <span className="text-secondary font-jost text-sm font-normal leading-5 lg:text-sm lg:font-semibold">
              {feature.split(",")[0]}
              <span className="font-normal">{feature.split(",")[1]}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const getSubscriptionFooterData = () => <>{getFeatures()}</>;

  const planType = function (type: string) {
    if (type === "Essential") {
      return "border-transparent bg-transparent";
    } else if (type === "Premium") {
      return "border-[#3E5FFF] bg-[#3E5FFF]";
    } else if (type === "Ultimate") {
      return "border-[#FC9E2F] bg-[#FC9E2F]";
    }
  };

  return (
    <div id={subscriptionName} className={`flex flex-col rounded-3xl size-full ${planType(subscriptionName)}`}>
      {getSubscriptionHeaderData()}
      <div
        className={`grow !bg-white pt-6 px-6 pb-8 lg:py-[31px] rounded-3xl border-[3px] ${planType(subscriptionName)}`}>
        {getSubscriptionBodyData()}
        {getSubscriptionFooterData()}
      </div>
    </div>
  );
};

export default Subscription;
